import { Component, inject, OnInit } from '@angular/core';
import { API, NotificationCategoryDTO, NotificationDTO } from 'src/app/services/api/api';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { forkJoin } from 'rxjs';
import { NotificationService } from 'src/app/services/notifications/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { QueryParamService } from 'src/app/services/lists/query-params';
import { InitialToggles, MsOptions, TableWrapperOptions } from '../../services/global/global-interfaces';
import { PermissionService } from '../../services/permission.service';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-page-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false,
})
export class NotificationsComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  DEBUGGING: boolean = false;

  notifications: NotificationDTO[];
  notificationCategories: NotificationCategoryDTO[];
  filteredCategories: NotificationCategoryDTO[];
  queryParamsParsed: boolean;

  initialToggles: InitialToggles[];
  archived?: boolean;
  read?: boolean;

  loading: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;

  options: TableWrapperOptions;
  msCategoryOptions: MsOptions;

  constructor(
    private readonly api: API,
    private readonly queryParamService: QueryParamService,
    private readonly modalNavigation: ModalNavigationService,
    private readonly notificationService: NotificationService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.DEBUGGING = false;
    this.notifications = [];
    this.notificationCategories = [];
    this.filteredCategories = [];
    this.queryParamsParsed = false;
    this.archived = undefined;
    this.read = undefined;
    this.loading = true;
    this.debounceTimer = null;
  }

  ngOnInit() {
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.queryParamsParsed = true;
      });
    }
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }

    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    if (this.options?.pagination) {
      this.loading = true;
      this.api
        .shared_Notification_retrieveAll(
          {
            search: this.stringTransformation.encodeSearchQueryParam(this.options.exactMatch, this.options.search),
            archived: this.archived,
            read: this.read,
            category: this.util.arrayToIdArray(this.filteredCategories),
          },
          this.options.pagination
        )
        .subscribe({
          next: (receivedNotifications: NotificationDTO[]) => {
            this.notifications = receivedNotifications;
            this.loading = false;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  checkAll() {
    if (this.DEBUGGING) {
      this.loading = true;
      this.api
        .shared_Notification_retrieveAll(
          {
            archived: false,
            read: false,
          },
          this.options.pagination
        )
        .subscribe((data: NotificationDTO[]) => {
          if (data.length > 0) {
            const obSes = [];
            for (const dto of data) {
              obSes.push(this.api.shared_Notification_readById(dto.id));
            }
            forkJoin(obSes).subscribe(() => {
              this.notifications = data;
              this.loading = false;
              this.checkAll();
            });
          } else {
            this.notifications = data;
            this.loading = false;
          }
        });
    }
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      this.parseToggleParams();
      this.parseFilterQueryParams().then(() => {
        res();
      });
    });
  }

  parseToggleParams() {
    this.initialToggles = [
      {
        id: 1,
        name: 'isArchived',
        text: this.translate.instant('lang.attributeToggles.notification.isArchived'),
        value: -1,
      },
      {
        id: 2,
        name: 'isRead',
        text: this.translate.instant('lang.attributeToggles.notification.isRead'),
        value: -1,
      },
    ];

    for (const e of this.initialToggles) {
      const param = this.queryParamService.getQueryParam(e.name);
      let ok = false;
      if (param) {
        try {
          if (parseInt(param, 10) === -1) {
            e.value = -1;
            ok = true;
          }
        } catch (e) {}
        try {
          if (parseInt(param, 10) === 0) {
            e.value = 0;
            ok = true;
          }
        } catch (e) {}
        try {
          if (parseInt(param, 10) === 1) {
            e.value = 1;
            ok = true;
          }
        } catch (e) {}
        if (!ok) {
          e.value = -1;
        }
      } else {
        e.value = -1;
      }
    }
  }

  parseFilterQueryParams(): Promise<void> {
    return new Promise(res => {
      const categoriesQp = this.queryParamService.getQueryParam('categories');

      let categoryIds: string[] = [];

      if (categoriesQp && categoriesQp.length > 0) {
        categoryIds = JSON.parse(categoriesQp);
      }

      const observablesList = [];

      if (categoryIds.length > 0) {
        observablesList.push(this.api.shared_Notification_retrieveCategories({ id: categoryIds }));
      }

      forkJoin(observablesList).subscribe(resultArray => {
        const categories: NotificationCategoryDTO[] = [];

        if (resultArray && resultArray.length > 0) {
          for (const resultArrayElement of resultArray) {
            const arr: any[] = resultArrayElement as any[];
            for (const item of arr) {
              switch (item._type) {
                case 'NotificationCategoryDTO':
                  categories.push(item);
                  break;
              }
            }
          }
          this.filteredCategories = categories;
          res();
        } else {
          res();
        }
      });

      if (observablesList.length <= 0) {
        res();
      }
    });
  }

  refreshQueryParams() {
    if (this.filteredCategories && this.filteredCategories.length > 0) {
      this.queryParamService.setQueryParam('categories', JSON.stringify(this.util.arrayToIdArray(this.filteredCategories)));
    } else {
      this.queryParamService.clearQueryParam('categories');
    }

    this.refreshToggleQueryParam('isRead', this.read === true ? 1 : this.read === false ? 0 : -1);
    this.refreshToggleQueryParam('isArchived', this.archived === true ? 1 : this.archived === false ? 0 : -1);
  }

  refreshBoolQueryParam(name: string, value: boolean) {
    if (value === true || value === false) {
      this.queryParamService.setQueryParam(name, '' + value);
    } else {
      this.queryParamService.clearQueryParam(name);
    }
  }

  refreshToggleQueryParam(name: string, value: number) {
    if (value === 1 || value === 0) {
      this.queryParamService.setQueryParam(name, '' + value);
    } else {
      this.queryParamService.clearQueryParam(name);
    }
  }

  toggleChange(evt: any) {
    for (const e of evt) {
      if (e.name === 'isArchived') {
        this.archivedChange(e.value);
      }
      if (e.name === 'isRead') {
        this.readChange(e.value);
      }
    }
    this.requestDebounced();
  }

  archivedChange(e) {
    switch (e) {
      case null:
        this.archived = null;
        break;
      case -1:
        this.archived = null;
        break;
      case 0:
        this.archived = false;
        break;
      case 1:
        this.archived = true;
        break;
    }
  }

  readChange(e) {
    switch (e) {
      case null:
        this.read = null;
        break;
      case -1:
        this.read = null;
        break;
      case 0:
        this.read = false;
        break;
      case 1:
        this.read = true;
        break;
    }
  }

  check(notification: NotificationDTO) {
    this.notificationService
      .markAsRead(notification)
      .then(() => {
        this.requestDebounced();
      })
      .catch(error => {
        this.errorHandler.errorHandler(error);
        this.requestDebounced();
      });
  }

  archive(notification: NotificationDTO) {
    this.notificationService
      .archive(notification)
      .then(() => {
        this.requestDebounced();
      })
      .catch(error => {
        this.errorHandler.errorHandler(error);
        this.requestDebounced();
      });
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'NOTIFICATION_CATEGORIES':
        if (options) {
          this.msCategoryOptions = options;
        }
        if (this.msCategoryOptions && this.msCategoryOptions.pagination) {
          this.api
            .shared_Notification_retrieveCategories(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msCategoryOptions?.search),
              },
              this.msCategoryOptions?.pagination
            )
            .subscribe(
              (categories: NotificationCategoryDTO[]) => {
                this.notificationCategories = categories;
              },
              error => {
                this.errorHandler.errorHandler(error);
              }
            );
        }
        break;
    }
  }

  update($event, type: string) {
    switch (type) {
      case 'NOTIFICATION_CATEGORIES':
        if ($event && $event.length > 0) {
          this.filteredCategories = $event;
        } else {
          this.filteredCategories = [];
        }
        break;
    }
    this.refreshFromServer();
  }
}
